import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { AgGridReact } from "ag-grid-react"
import type { CellClickedEvent, GridOptions } from "ag-grid-community"
import { AnimatePresence } from "motion/react"
import { ArrowsInSimple, ArrowsOutSimple, FunnelSimple, SquaresFour } from "@phosphor-icons/react"

import { IconButton, Select } from "~/components/design-system/components"
import { DisplayOptionsPopover } from "~/components/planning/DisplayOptionsPopover"
import ExpandableSearchField from "~/components/planning/ExpandableSearchField"
import FilterPopover from "~/components/planning/FilterPopover"
import { useRegisterTable } from "~/components/planning/TableRegistryContext"
import { FilterRowAnimated } from "~/components/planning/ViewConfiguration"
import { DataTableBaseV2 } from "~/components/reusable/DataTableV2"
import { Tooltip } from "~/components/reusable/Tooltip"
import { FilterFieldsOverrideContext } from "~/hooks/useFilterFieldsOverride"
import { useProductTableDisplayColumns } from "~/hooks/useProductTable"
import { FilterScopeProvider } from "~/providers/FilterScopeProvider"
import { GroupByDisplayOption } from "../../AllocationFilters"
import { DraftCommandbar } from "../../DraftCommandbar"
import ProductSettingsChangelog from "./ProductSettingsChangelog"
import ProductSettingsDialog from "./ProductSettingsDialog"
import { autoGroupColumn, COLUMNS, GRID_OPTIONS, type GridContext } from "./productColumns"
import {
  byCatalogueName,
  productRows,
  useRowCaches,
  useVariantSummaries,
  type GroupBy,
  type Row,
  type RowScope,
} from "./productRows"
import type { DialogView, ProductSettingsDraft } from "./productSettingsModel"
import { isSettingField, SETTING_FIELD_META, type DialogTab } from "./productSettingFields"
import { useIncrementalRowData } from "./useIncrementalRowData"
import { useProductFilters, useVariantExternalFilter, type OverrideView } from "./useProductFilters"
import {
  productSettingsChanges,
  useProductSettingsData,
  useProductSettingsDraft,
} from "./useProductSettings"
import { useStoreGroups, type StoreGroupStore } from "./storeGroups"
import { VARIANT_SEARCH_OPTIONS } from "~/utils/lineSearch"

const TABLE_ID = "allocation-settings-products"

const OVERRIDE_VIEWS: { key: OverrideView; label: string }[] = [
  { key: "all", label: "All variants" },
  { key: "with", label: "Has overrides" },
  { key: "without", label: "No overrides" },
]

const GROUP_OPTIONS: { key: GroupBy; label: string }[] = [
  { key: "variant", label: "Variant" },
  { key: "product", label: "Product" },
  { key: "package", label: "Package" },
  { key: "store", label: "Store" },
]

/** Every store x SKU can be set; the package view narrows it to the pairs a package sends. */
type StoreReach = "everywhere" | "packages"

const REACH_OPTIONS: { value: StoreReach; label: string }[] = [
  { value: "packages", label: "Package stores only" },
  { value: "everywhere", label: "Every store" },
]

const NON_REMOVABLE_COLUMNS = ["productSettings"]

const CHOOSER_COLUMNS = COLUMNS.map(({ group, def }) => ({
  id: def.colId,
  displayName: def.headerName ?? def.colId,
  group,
}))
const ALL_COLUMN_IDS = CHOOSER_COLUMNS.map((column) => column.id)

type DialogState = {
  variantId: string
  tab: DialogTab
  /** Opened under a store or package group: the dialog's Current view fill scope. */
  view?: DialogView
  /** The grid keeps showing this while the dialog is open, and Cancel puts it back. */
  draftAtOpen: ProductSettingsDraft
}

function expandToggleTitle(blocked: string | undefined, expanded: boolean): string {
  if (blocked) return blocked
  return expanded ? "Collapse all" : "Expand all"
}

/** The stores of the group a dialog was opened from, named for the Current view scope. */
function dialogViewOf(
  scope: RowScope | undefined,
  packages: { id: string; name: string; storeIds: string[] }[],
  stores: readonly StoreGroupStore[],
): DialogView | undefined {
  if (scope?.kind === "package") {
    const pkg = packages.find((candidate) => candidate.id === scope.id)
    return pkg && { label: pkg.name, storeIds: pkg.storeIds }
  }
  if (scope?.kind === "store") {
    const store = stores.find((candidate) => candidate.id === scope.id)
    return store && { label: store.storeNumber, storeIds: [store.id] }
  }
  return undefined
}

/** Settings › Products: per variant, the store x size settings and the dialog that edits them. */
export default function SetupProducts() {
  return (
    <FilterScopeProvider scopeKey={TABLE_ID}>
      <ProductsTable />
    </FilterScopeProvider>
  )
}

function ProductsTable() {
  const [reach, setReach] = useState<StoreReach>("packages")
  const data = useProductSettingsData(reach === "everywhere")
  const { packages, stores, everywhere, batches, saved, emptyMessage, isLoading } = data
  const listings = reach === "everywhere" ? everywhere : data.listings
  const [groupBy, setGroupBy] = useState<GroupBy>("variant")
  const [expanded, setExpanded] = useState(false)
  const [dialog, setDialog] = useState<DialogState | null>(null)
  const gridRef = useRef<AgGridReact<Row>>(null)

  const settingsDraft = useProductSettingsDraft(saved, batches)
  const { draft } = settingsDraft
  // Read when a dialog opens, from handlers the grid holds on to.
  const latestDraft = useRef(draft)
  useEffect(() => {
    latestDraft.current = draft
  }, [draft])
  // The grid sits behind the open dialog, so it takes the dialog's edits in one go when it closes.
  const gridDraft = dialog?.draftAtOpen ?? draft

  const ordered = useMemo(() => [...listings.values()].sort(byCatalogueName), [listings])
  const storeGroups = useStoreGroups({ groupBy, ordered, stores })
  const caches = useRowCaches(saved, listings, data.listings, data.scoped)
  const summaries = useVariantSummaries(caches, gridDraft, storeGroups.storeListings)

  // One array per variant, so a row can tell its package names are unchanged by identity.
  const packageNamesOf = useMemo(() => {
    const name = new Map(packages.map((pkg) => [pkg.id, pkg.name]))
    return new Map(
      [...listings.values()].map((listing) => [
        listing.variantId,
        listing.packageIds.map((id) => name.get(id) ?? id),
      ]),
    )
  }, [packages, listings])

  const {
    search,
    setSearch,
    searchBy,
    setSearchBy,
    filters,
    setFilters,
    overrideView,
    setOverrideView,
    viewCounts,
    filterFieldsOverride,
    visibleVariants,
    filtering,
  } = useProductFilters({ tableId: TABLE_ID, listings, summaries, packages, packageNamesOf })
  const viewOptions = useMemo(
    () =>
      OVERRIDE_VIEWS.map((view) => ({
        value: view.key,
        label: isLoading
          ? view.label
          : `${view.label} · ${viewCounts[view.key].toLocaleString("en")}`,
        icon: <SquaresFour />,
      })),
    [viewCounts, isLoading],
  )

  const { rows, totalRow, listedVariantIds } = useMemo(
    () =>
      productRows({
        caches,
        summaries,
        storeGroups,
        ordered,
        packages,
        stores,
        packageNamesOf,
        groupBy,
        visibleVariants,
      }),
    [
      caches,
      summaries,
      storeGroups,
      ordered,
      packages,
      stores,
      packageNamesOf,
      groupBy,
      visibleVariants,
    ],
  )

  const externalFilter = useVariantExternalFilter(gridRef, filtering, visibleVariants)
  const gridRows = useIncrementalRowData(gridRef, rows)

  // Pinned rows are only read at init; set them again when the totals change.
  const pinTotal = useCallback(() => {
    gridRef.current?.api?.setGridOption("pinnedBottomRowData", rows.length > 0 ? [totalRow] : [])
  }, [rows.length, totalRow])
  useEffect(pinTotal, [pinTotal])

  // A search opens the groups, so the matches show without expanding each one.
  const { expandAllBlocked } = storeGroups
  useEffect(() => {
    const api = gridRef.current?.api
    if (!api || search === "" || expandAllBlocked) return
    api.forEachNode((node) => {
      if (node.level === 0) node.setExpanded(true)
    })
  }, [search, rows, expandAllBlocked])

  const [displayedColumnIds, setDisplayedColumnIds] = useProductTableDisplayColumns(
    TABLE_ID,
    ALL_COLUMN_IDS,
    "local",
  )
  const columnDefs = useMemo(() => {
    const shown = new Set(displayedColumnIds)
    return COLUMNS.map(({ def }) =>
      NON_REMOVABLE_COLUMNS.includes(def.colId) ? def : { ...def, hide: !shown.has(def.colId) },
    )
  }, [displayedColumnIds])
  useRegisterTable(TABLE_ID, {
    columnChooserDialogProps: {
      columnIds: ALL_COLUMN_IDS,
      displayedColumnIds,
      setDisplayedColumnIds,
      columnMetadata: CHOOSER_COLUMNS,
      nonRemovableColumns: NON_REMOVABLE_COLUMNS,
      defaultColumnIds: ALL_COLUMN_IDS,
      storageKey: `${TABLE_ID}-columns`,
    },
  })
  const autoGroupColumnDef = useMemo(() => autoGroupColumn(groupBy), [groupBy])

  const changes = useMemo(() => {
    if (!saved) return []
    const storeName = new Map(
      stores.map((store) => [store.allocationLocationId, store.storeNumber]),
    )
    // Every variant, so a change keeps its name whichever stores the table lists.
    const skuLabel = new Map(
      Object.keys(draft).flatMap((variantId) => {
        const listing = everywhere.get(variantId)
        if (!listing) return []
        return listing.skus.map((sku) => [sku.skuId, `${listing.name} ${sku.size}`] as const)
      }),
    )
    return productSettingsChanges(draft, saved, {
      variantName: (id) => everywhere.get(id)?.name ?? id,
      skuLabel: (id) => skuLabel.get(id) ?? id,
      storeName: (id) => storeName.get(id) ?? id,
    })
  }, [draft, saved, everywhere, stores])

  const openDialog = useCallback(
    (variantId: string, tab: DialogTab, scope?: RowScope) =>
      setDialog({
        variantId,
        tab,
        view: dialogViewOf(scope, packages, stores),
        draftAtOpen: latestDraft.current,
      }),
    [packages, stores],
  )
  const { onRowGroupOpened } = storeGroups
  const context = useMemo<GridContext>(() => ({ open: openDialog }), [openDialog])
  const gridOptions = useMemo<GridOptions<Row>>(
    () => ({ ...GRID_OPTIONS, ...externalFilter, onRowGroupOpened }),
    [externalFilter, onRowGroupOpened],
  )
  const onCellClicked = useCallback(
    ({ column, data }: CellClickedEvent<Row>) => {
      const field = column.getColId()
      if (!isSettingField(field) || data?.kind !== "variant") return
      openDialog(data.listing.variantId, SETTING_FIELD_META[field].tab, data.scope)
    },
    [openDialog],
  )
  // Every store and size, whichever view the table is in: any store x SKU can be set.
  const dialogListing = dialog ? everywhere.get(dialog.variantId) : undefined

  return (
    <FilterFieldsOverrideContext.Provider value={filterFieldsOverride}>
      <div className="flex min-h-0 flex-1 flex-col gap-2">
        <div className="flex items-center gap-2">
          <Select
            width={200}
            aria-label="Stores listed"
            isSearchable={false}
            options={REACH_OPTIONS}
            value={REACH_OPTIONS.find((option) => option.value === reach) ?? null}
            onChange={(option) => option && setReach(option.value)}
          />
          <Select
            width={200}
            aria-label="Products view"
            isSearchable={false}
            options={viewOptions}
            defaultValue={viewOptions[0]}
            value={viewOptions.find((option) => option.value === overrideView) ?? null}
            onChange={(option) => option && setOverrideView(option.value)}
          />
          <div className="flex-1" />
          <Tooltip title={expandToggleTitle(expandAllBlocked, expanded)}>
            {/* A disabled button gets no hover, so the span carries the tooltip. */}
            <span className="inline-flex">
              <IconButton
                variant="neutral"
                size="sm"
                disabled={expandAllBlocked !== undefined}
                aria-label={expanded ? "Collapse all" : "Expand all"}
                onClick={() => {
                  const api = gridRef.current?.api
                  if (expanded) api?.collapseAll()
                  else api?.expandAll()
                  setExpanded(!expanded)
                }}
              >
                {expanded ? <ArrowsInSimple /> : <ArrowsOutSimple />}
              </IconButton>
            </span>
          </Tooltip>
          <ExpandableSearchField
            initialSearchText={search}
            onSearchTextChange={setSearch}
            placeholder="Search..."
            debounceTime={200}
            searchBy={searchBy}
            onSearchByChange={setSearchBy}
            searchByOptions={VARIANT_SEARCH_OPTIONS}
          />
          <FilterPopover
            align="end"
            shortcutEnabled
            customTrigger={
              <IconButton variant="neutral" size="sm" aria-label="Add filter">
                <FunnelSimple />
              </IconButton>
            }
          />
          <DisplayOptionsPopover
            tableId={TABLE_ID}
            extraOptions={
              <div className="flex flex-col gap-3">
                <GroupByDisplayOption
                  options={GROUP_OPTIONS}
                  value={groupBy}
                  onChange={setGroupBy}
                />
              </div>
            }
          />
        </div>
        <AnimatePresence initial={false}>
          {filters.length > 0 && (
            <FilterRowAnimated
              filters={filters}
              supportsViews={false}
              isCurrentViewChanged={false}
              hasCurrentView={false}
              showClearView={false}
              backToDefault={() => setFilters([])}
            />
          )}
        </AnimatePresence>
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-base bg-base">
          <div className="flex min-h-0 flex-1 flex-col">
            <DataTableBaseV2<Row>
              id="allocation-settings-products"
              gridRef={gridRef}
              rowData={gridRows}
              columnDefs={columnDefs}
              autoGroupColumnDef={autoGroupColumnDef}
              gridOptions={gridOptions}
              context={context}
              onGridReady={pinTotal}
              onCellClicked={onCellClicked}
              compact
              loading={isLoading}
              showPagination={false}
              disableStatePersistence
              emptyMessage={emptyMessage}
            />
          </div>
          <ProductSettingsChangelog
            variantIds={listedVariantIds}
            listings={everywhere}
            stores={stores}
          />
        </div>

        {dialog && dialogListing && saved && (
          <ProductSettingsDialog
            listing={dialogListing}
            view={dialog.view}
            stores={stores}
            saved={saved}
            settingsDraft={settingsDraft}
            draftAtOpen={dialog.draftAtOpen}
            tab={dialog.tab}
            onTabChange={(tab) => setDialog({ ...dialog, tab })}
            onClose={() => setDialog(null)}
          />
        )}

        <DraftCommandbar
          changes={changes}
          saving={settingsDraft.saving}
          saveDisabledReason={changes.length > 0 ? settingsDraft.saveBlocker : undefined}
          onDiscard={settingsDraft.discard}
          onSave={settingsDraft.save}
        />
      </div>
    </FilterFieldsOverrideContext.Provider>
  )
}
